import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreateFeature,
  useUpdateFeature,
  type FeatureCategory,
  type FeatureRow,
} from "@/hooks/useFeatures";

const CATEGORIES: FeatureCategory[] = [
  "BUSINESS",
  "BOOKINGS",
  "CUSTOMER_ENGAGEMENT",
  "ANALYTICS",
  "SUPPORT",
  "MARKETING",
  "STAFF",
  "ADMIN",
  "REPORTS",
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: FeatureRow | null; // null = create
}

const emptyForm = {
  code: "",
  name: "",
  description: "",
  category: "BUSINESS" as FeatureCategory,
  sortOrder: "0",
  isActive: true,
};

export const FeatureEditDialog = ({ open, onOpenChange, feature }: Props) => {
  const createFeature = useCreateFeature();
  const updateFeature = useUpdateFeature();
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (feature) {
      setForm({
        code: feature.code,
        name: feature.name,
        description: feature.description ?? "",
        category: (feature.category as FeatureCategory) ?? "BUSINESS",
        sortOrder: String(feature.sortOrder ?? 0),
        isActive: feature.isActive,
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, feature]);

  const onSubmit = async () => {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      category: form.category,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
    };
    if (!payload.code || !payload.name) {
      toast.error("Code and name are required");
      return;
    }
    try {
      if (feature) {
        await updateFeature.mutateAsync({ id: feature.id, data: payload });
      } else {
        await createFeature.mutateAsync(payload);
      }
      toast.success(feature ? "Feature updated" : "Feature created");
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const isSaving = createFeature.isPending || updateFeature.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {feature ? `Edit ${feature.name}` : "Add feature"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code (slug)">
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="business.listing"
                disabled={!!feature}
              />
            </Field>
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Business listing"
              />
            </Field>
          </div>
          <Field label="Category">
            <Select
              value={form.category}
              onValueChange={(v) =>
                setForm({ ...form, category: v as FeatureCategory })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Description">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order">
              <Input
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({ ...form, sortOrder: e.target.value })
                }
              />
            </Field>
            <label className="flex items-center gap-2 text-sm mt-6 cursor-pointer">
              <Switch
                checked={form.isActive}
                onCheckedChange={(v) => setForm({ ...form, isActive: v })}
              />
              <span>Active</span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSaving}>
            {isSaving ? "Saving…" : feature ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default FeatureEditDialog;
